import React, { useRef, useState, useEffect } from 'react';
import { Order } from '../../../types';
import api from '../../../api/client';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number | null; // ✅ تغيير من order إلى orderId
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ isOpen, onClose, orderId }) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // جلب تفاصيل الطلب عند فتح المودال
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!isOpen || !orderId) return;
      
      setLoading(true);
      setError(null);
      setOrder(null);

      try {
        const response = await api.get<Order>(`/orders/${orderId}/`);
        setOrder(response.data);
      } catch (err: any) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [isOpen, orderId]);
const handleDownloadBill = async () => {
  if (!order) return;
  setDownloading(true);
  setDownloadError(null);

  try {
    const response = await api.get(`/orders/${order.id}/bill/`, {
      responseType: 'blob',
    });

    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data as BlobPart], { type: 'application/pdf' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `order_${order.id}_bill.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error('Download error:', err);
    setDownloadError('Failed to download bill. Please try again.');
  } finally {
    setDownloading(false);
  }
};


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !downloading && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-details-title"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading order details...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 bg-red-50 p-3 rounded mb-4" role="alert">
              {error}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        ) : order ? (
          <>
            <h3 id="order-details-title" className="text-xl font-semibold mb-4">
              Order #{order.id} Details
            </h3>
            
            {downloadError && (
              <p className="text-red-600 bg-red-50 p-2 rounded mb-4" role="alert">
                {downloadError}
              </p>
            )}
            
            <div className="space-y-4">
              {/* معلومات الطلب الأساسية */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Customer:</strong> {order.customer?.email || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Payment Status:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                      order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {order.payment_status?.charAt(0).toUpperCase() + order.payment_status?.slice(1) || 'N/A'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Payment Method:</strong> {order.payment_method || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Status:</strong>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                      order.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                      order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || 'N/A'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Total:</strong> ${parseFloat(order.total?.toString() || '0').toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Created:</strong> {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* عنوان الشحن */}
              {order.address && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-lg mb-2">Shipping Address</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {order.address.street && <p><strong>Street:</strong> {order.address.street}</p>}
                    {order.address.neighborhood && <p><strong>Neighborhood:</strong> {order.address.neighborhood}</p>}
                    {order.address.city && <p><strong>City:</strong> {order.address.city}</p>}
                    {order.address.compound && <p><strong>Compound:</strong> {order.address.compound}</p>}
                    {order.address.building_or_house && <p><strong>Building/House:</strong> {order.address.building_or_house}</p>}
                    {order.address.mobile && <p><strong>Phone:</strong> {order.address.mobile}</p>}
                  </div>
                </div>
              )}

              {/* تفاصيل المنتجات */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-lg mb-2">Items ({order.items?.length || 0})</h4>
                {order.items && order.items.length > 0 ? (
                  <table className="min-w-full text-sm text-left text-gray-700">
                    <thead className="bg-gray-100 text-xs uppercase font-semibold text-gray-600">
                      <tr>
                        <th className="px-4 py-2" scope="col">Product</th>
                        <th className="px-4 py-2" scope="col">Quantity</th>
                        <th className="px-4 py-2" scope="col">Price</th>
                        <th className="px-4 py-2" scope="col">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {order.items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2">
                            <div className="flex items-center">
                              {item.product?.images?.[0]?.image && (
                                <img
                                  src={item.product.images[0].image}
                                  alt={item.product.name}
                                  className="w-10 h-10 object-cover rounded mr-3"
                                />
                              )}
                              <div>
                                <p className="font-medium">{item.product?.name || 'N/A'}</p>
                                {item.product?.sku && (
                                  <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2">{item.quantity}</td>
                          <td className="px-4 py-2">${parseFloat(`${item.price}` || '0').toFixed(2)}</td>
                          <td className="px-4 py-2">
                            ${(parseFloat(`${item.price}` || '0') * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td colSpan={3} className="px-4 py-2 text-right">Total:</td>
                        <td className="px-4 py-2">
                          ${parseFloat(order.total?.toString() || '0').toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <p className="text-gray-500 text-center py-4">No items found for this order.</p>
                )}
              </div>

              {/* الأزرار */}
              <div className="flex justify-between items-center border-t pt-4">
                <button
                  onClick={handleDownloadBill}
                  className="bg-primary text-white px-4 py-2 rounded text-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  disabled={downloading || !order}
                  aria-label="Download order bill as PDF"
                >
                  {downloading ? 'Downloading...' : 'Download Bill'}
                </button>
                <button
                  ref={closeButtonRef}
                  onClick={onClose}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  aria-label="Close order details"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No order details available.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailsModal;